/**
 * 输入框里的模型与推理强度选择器（W16a / W16b / W16c / W19）。
 *
 * 整份只做一件事，而这件事全在边界上：
 * **它只控制「和你对话的那个模型」，不创建 Agent、不改团队、
 * 不打断正在跑的 Agent。**
 *
 * 设计稿把这句话写进下拉里，因为用户在这一刻最容易以为自己在换团队。
 */
import type { TeamMember } from './roster'

export type Effort = 'fast' | 'standard' | 'max'

export const EFFORTS = Object.freeze(['fast', 'standard', 'max'] as const)

export const EFFORT_COPY: Readonly<Record<Effort, string>> = Object.freeze({
  fast: 'lulu_workbench.model.effort_fast',
  standard: 'lulu_workbench.model.effort_standard',
  max: 'lulu_workbench.model.effort_max',
})

/**
 * 模型此刻的真实状态。**七档逐字来自 openapi 的 `LuluAgent.model_status`**，
 * 真源是 orchestrator 的候选表（与「安排开发」同一个）。
 *
 * 上一轮这里是自己编的三档（ok/expired/quota）。**编的那三档最大的问题
 * 不是少，是它把七种不同的原因压成两句话**——而用户看到「用不了」之后
 * 要做的下一件事，在「没贴 Key」和「adapter 还没实现」之间完全不同：
 * 前者他自己能修，后者只能等我们。
 */
export type ModelStatus =
  | 'ok'
  | 'unverified'
  | 'unavailable'
  | 'rate_limited'
  | 'quota_exhausted'
  | 'disabled'
  | 'not_wired'
  | 'unlisted'
  | 'unset'
  | 'unknown'

export const MODEL_STATUSES = Object.freeze([
  'ok',
  'unverified',
  'unavailable',
  'rate_limited',
  'quota_exhausted',
  'disabled',
  'not_wired',
  'unlisted',
  'unset',
  'unknown',
] as const)

/**
 * 染色。openapi 的注释给了建议，这里逐字照做：
 * `no_key`、`not_callable` → 红；`not_implemented`、`unlisted` → 黄。
 *
 * 剩下三档单独说：
 *   - `ok` 不染色
 *   - `unset`（这个 Agent 还没配模型）→ 黄。它不是故障，是**没配**，
 *     而没配是用户自己能解决的事
 *   - `unknown`（问不到注册表）→ 黄，并且**必须显示它是「问不到」**。
 *     把 unknown 画成红，会让用户以为模型坏了然后去重贴 Key；
 *     画成 ok 更糟，那是拿一个我们不知道的事当好消息。
 */
export type StatusTone = 'none' | 'warn' | 'bad'

export function toneForStatus(status: ModelStatus): StatusTone {
  switch (status) {
    case 'ok':
      return 'none'
    case 'unavailable':
    case 'quota_exhausted':
    case 'disabled':
      return 'bad'
    case 'unverified':
    case 'rate_limited':
    case 'not_wired':
    case 'unlisted':
    case 'unset':
    case 'unknown':
      return 'warn'
  }
}

export interface ModelOption {
  readonly id: string
  readonly name: string
  readonly status: ModelStatus
  /**
   * 服务端给的原话（`model_status_reason`）。**探针原话，不是我们编的。**
   * 空串表示服务端没给——那时只显示档位对应的那句，不留白。
   */
  readonly reason: string
}

export interface ChatModelChoice {
  /**
   * 与后端 `ComposerModelEnvelope.policy` **同一套词**：`auto | fixed`。
   *
   * 上一轮这里写的是 `'auto' | 'pinned'`，界面词。改成 wire 词是因为
   * 这个字段要原样发给 `POST /conversations/{id}/model`——留着两份词表，
   * 就得有一层翻译，而这个仓库刚在 solo|single 上为同一件事付过一次代价。
   * 界面上那个「固定」的字样由文案模板给（`model.pinned`），不由类型给。
   */
  readonly mode: 'auto' | 'fixed'
  readonly modelId: string | null
  /** null = 没选，让服务端按功能位默认（今天对话「快速」、拆计划「标准」）。 */
  readonly effort: Effort | null
}

export const DEFAULT_CHOICE: ChatModelChoice = Object.freeze({
  mode: 'auto',
  modelId: null,
  effort: null,
})

/**
 * 改这个选择会不会动到团队。
 *
 * **恒为 false，没有入参。** 验收 #29 要的就是这句话，而把它写成
 * 一个函数（而不是注释）是为了让它有个能红的落点。
 */
export function affectsTeam(): false {
  return false
}

/**
 * 改这个选择会不会打断正在跑的 Agent。
 *
 * 同上，恒 false。设计稿 W16b 那两行「团队里 4 位 Agent 用的模型，
 * 一个都不变」「正在跑的活不打断，也不换」说的就是这两个函数。
 */
export function interruptsRunning(): false {
  return false
}

/**
 * 换了模型之后，团队成员列表应该原样不动。
 *
 * 回的是**同一个数组引用**——引用相等这件事比「内容相等」更强，
 * 而这里要的正是更强的那个：一次「看起来一样但重建了」的更新，
 * 会让运行中的 Agent 行重新挂载，那在界面上就是一次闪烁，
 * 而用户会以为发生了什么。
 */
export function teamAfterModelChange(members: readonly TeamMember[]): readonly TeamMember[] {
  return members
}

/**
 * 固定的模型不可用时怎么办。
 *
 * **只有一个答案：停下来问。** 返回类型刻意只有 `'ask'` 一个值——
 * 留下 `'fallback'` 那一支，就是留下一条静默换模型的路，
 * 而设计稿第一句就是「换了结果可能不一样而你不知道」。
 */
export function onPinnedUnavailable(): 'ask' {
  return 'ask'
}

/**
 * 不可用的模型要不要从列表里拿掉。
 *
 * **不拿掉。** 验收 #31：授权过期、额度用完的都要列出来、标红、
 * 但不隐藏。隐藏会让用户以为这个模型不存在，然后去重新接一次线。
 */
export function visibleModels(all: readonly ModelOption[]): readonly ModelOption[] {
  return all
}

/**
 * 一个模型该显示哪句原因。
 *
 * **优先用服务端的原话。** 我们的模板是兜底，不是覆盖——探针说
 * 「套餐里没有这个模型」比我们说「额度用完了」准确得多，而后者
 * 会让用户去充值一个不需要充的账户。
 */
export function reasonTextFor(option: ModelOption): string | null {
  if (option.status === 'ok') return null
  return option.reason.trim() === '' ? null : option.reason
}

/**
 * 未验证的模型能不能自动接到重要的活。
 *
 * **不能。** 验收 #32。注意它与「可不可选」是两件事：用户可以主动选它，
 * 平台不会主动派活给它。把两者合成一个布尔值，就会得出
 * 「未验证 = 不可选」，而那不是设计稿要的。
 */
export function mayAutoAssignImportantWork(option: ModelOption): boolean {
  return option.status === 'ok'
}

/**
 * 用户能不能主动选它。
 *
 * **两档红的不能选；四档黄的能选。** 黄那几档（未实现、不在注册表、
 * 没配、问不到）的共同点是「我们不确定它不行」，而验收 §32 要的正是
 * 「可见、可选、只是不自动派重要活」。把黄的也禁掉，就把
 * 「我们没验过」变成了「你不能用」。
 */
export function mayUserPick(option: ModelOption): boolean {
  return option.status === 'ok'
}
